import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { orderBy } from 'lodash';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { GenderPipe } from 'src/app/moov-common/pipes/gender.pipe';
import { CultureStateAggData, DriverStateAggData, TeamStateAggData } from 'src/app/moov-common/services/data/data-data';
import { AbstractDataService } from 'src/app/moov-common/services/data/data.service.abstract';
import { AbstractLocalStorageService } from 'src/app/moov-common/services/localStorage/local-storage.abstract.service';
import { LocalStorageKeys } from 'src/app/moov-common/services/localStorage/local-storage.enum';
import { UserCreatedOrgData, UserMicroData } from 'src/app/moov-common/services/personal-user-data/personal-user-data';
import { AbstractPersonalUserDataService } from 'src/app/moov-common/services/personal-user-data/personal-user-data.abstract.service';
import { FilterDataOption, FilterOrgValueOptions } from 'src/app/moov-common/services/utils/utils-data';
import { TeamViewFiltersComponent } from 'src/app/team-view/team-view-filters/team-view-filters.component';
import { Subscription } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { Locale } from 'src/app/moov-common/enum/locale.enum';
import { ClaroEvents } from 'src/app/moov-common/services/google-analytics/analytics.enum';


@Component({
  selector: 'app-state-view',
  templateUrl: './state-view.component.html',
  styleUrls: ['./state-view.component.scss'],
})
export class StateViewComponent extends BaseComponent implements OnInit {
  presentPastScore: boolean = true;
  selectedCultureDrivers: DriverStateAggData[] = [];
  isLoadingDrivers: boolean = false;
  stateViewData: TeamStateAggData | null;
  managersBreakdownList: UserMicroData[] = [];
  managersFilterList: FilterDataOption[] = [];
  resellerOrgsFilterList: FilterDataOption[] = [];
  resellerOrgsList: UserCreatedOrgData[] = [];
  selectedCultureId: string = '';
  selectedCulture: CultureStateAggData | null | undefined = null;
  assetsRootFolder: string = 'assets/state/';
  isStateDataLoading: boolean = true;
  @ViewChild('stateViewSideNav') sideNavRef: MatSidenav;
  private dataSubscription: Subscription;
  stateViewDashboardHeader :string = "";
  selectedManagerId : string = "";

  constructor(
    private readonly dataService: AbstractDataService,
    private readonly personalUserDataService: AbstractPersonalUserDataService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private genderSerivce: GenderPipe,
    private localStorage: AbstractLocalStorageService
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.getAggData();
    this.analytics.sendClaroEvent(ClaroEvents.TeamStateDashboard_viewed, {});
  }

  getAggData(): void {
    this.isStateDataLoading = true;
    this.selectedCultureId = '';
    const orgOverride: string = this.getOrgOverride();

    // console.log(`SV getAggData orgOVerride is ${orgOverride}`);

    this.dataSubscription = this.dataService
      .getTeamStateAggData(this.selectedManagerId, orgOverride)
      .subscribe((teamStateData) => {
        teamStateData.culturesData = orderBy(
          teamStateData.culturesData,
          'impact',
          'desc'
        );
        this.stateViewData = teamStateData;
        if (this.selectedManagerId == '') {
          // This aggData was not filtered by manager, so we have the whole list
          this.managersBreakdownList = this.stateViewData.managersData;
        }
        this.setStateViewManagerName();
        this.createManagerFilterData();
        this.createResllerOrgsFilterData();
        this.isStateDataLoading = false;
      });
  }

  setStateViewManagerName() : void{
    let selectedUserName = "";
    if (this.selectedManagerId == ""){
      // no manager is seleted, the data presented is of the logged in user
      selectedUserName = `${this.user?.firstName} ${this.user?.lastName}`;
    }
    else{
      // find the manager in the list of manager
      const selectedManagerDetails : UserMicroData | undefined= this.managersBreakdownList.find(manager => manager.id === this.selectedManagerId )
      
      if (selectedManagerDetails){
        selectedUserName = `${selectedManagerDetails.firstName} ${selectedManagerDetails.lastName}`;
      }
    }

    const orgOverride: string = this.getOrgOverride()
    if (orgOverride == ""){
      // language issues - header data is changing between heb and eng
      if (this.getLocale() == Locale.en_US){
        this.stateViewDashboardHeader = selectedUserName + this.translate.instant('stateView.groupNameHeader');
      }
      else{
        this.stateViewDashboardHeader = this.translate.instant('stateView.groupNameHeader') + ' ' + selectedUserName;
      }      
    }
    else{
      // this is not our org hence - present differnent message
      let orgDetails = this.resellerOrgsList.find(currOrg => currOrg.id == orgOverride);
      if (orgDetails){
        this.stateViewDashboardHeader = orgDetails.name;
      }
      else{
        this.stateViewDashboardHeader = this.translate.instant('stateView.resellerEmptyOrg');
      }
    }

    
  }
  
  updateDriverBySelectedCulture(): void {
    this.isLoadingDrivers = true;
    this.selectedCulture = this.stateViewData?.culturesData.find(
      (culture) => culture.cultureId === this.selectedCultureId
    );
    this.isLoadingDrivers = false;
  }

  onCultureSelectedHandler(cultureId: string): void {
    this.selectedCultureId = cultureId;
    this.updateDriverBySelectedCulture();
    this.sideNavRef.open();
    this.analytics.sendClaroEvent(ClaroEvents.TeamStateDashboard_cultureSelected, {
      // could call the culture id here, to see which culture is most important/clicked
    });
  }

  resellerChangeOrg() {
    this.personalUserDataService
      .getAllUserCreatedOrgs()
      .subscribe((orgsList) => {
        let orgList: FilterDataOption[] = [];
        orgList.push({name: this.translate.instant('stateView.myOrgFilter'), value: FilterOrgValueOptions.EMPTY});
        for (let currOrgData of orgsList) {
          orgList.push({ name: currOrgData.name, value: currOrgData.id });
        }

        let dialogRef = this.dialog.open(TeamViewFiltersComponent, {
          height: 'fit-content',
          width: 'fit-content',
          panelClass: 'claro-custom-dialog',
          data: {},
        });

        let componentInstance = dialogRef.componentInstance;
        componentInstance.filterDataList = orgList;
        componentInstance.filterHeader = this.translate.instant(
          this.genderSerivce.transform('teamView.orgListFilterHeader')
        );

        dialogRef.afterClosed().subscribe((selectedValue) => {
          if (selectedValue) {
            // empty value means no orgOverride
            this.changeOrg(selectedValue);
          }
        });
      });
  }

  createManagerFilterData():void{
    this.managersFilterList = [];

    let tempManagersFilterList = [];

    tempManagersFilterList.push({
      name: this.translate.instant('stateView.selectManager_PresentForAll'),
      value: '',
    });  

    for (let currManager of this.managersBreakdownList) {
      tempManagersFilterList.push({
        name: currManager.firstName + ' ' + currManager.lastName,
        value: currManager.id,
      });
    }
    
    // Sort managers list by name, keeping the first entries at the top
    const firstEntry = tempManagersFilterList.splice(0, 1);
    tempManagersFilterList.sort((a, b) => a.name.localeCompare(b.name));
    tempManagersFilterList.unshift(...firstEntry);

    this.managersFilterList = tempManagersFilterList;
  }

  createResllerOrgsFilterData(): void{
    if (!this.isReseller){
      return;
    }

    this.personalUserDataService.getAllUserCreatedOrgs().subscribe(orgsList => {
        this.resellerOrgsList = orgsList;
        this.resellerOrgsFilterList = [];
        this.resellerOrgsFilterList.push({name: this.translate.instant('stateView.selectOrg_PresentForMe'), value: FilterOrgValueOptions.EMPTY});
        for (let currOrgData of orgsList) {
          this.resellerOrgsFilterList.push({ name: currOrgData.name, value: currOrgData.id });
        }

        // check if there is already an org override as we need to set stateViewDashboardHeader
        const orgOverride: string = this.getOrgOverride()
        if (orgOverride != ""){
          // this is not our org hence - present differnent message
          let orgDetails = this.resellerOrgsList.find(currOrg => currOrg.id == orgOverride);
          if (orgDetails){
            this.stateViewDashboardHeader = orgDetails.name;
          }
          else{
            this.stateViewDashboardHeader = this.translate.instant('stateView.resellerEmptyOrg');
          }
        }
    });
  }

  resellerFilterByParent(): void {
    let mgrsList: FilterDataOption[] = [];

    mgrsList.push({
      name: this.translate.instant('teamViewFilter.showAll'),
      value: '',
    });

    for (let currManager of this.managersBreakdownList) {
      mgrsList.push({
        name: currManager.firstName + ' ' + currManager.lastName,
        value: currManager.id,
      });
    }

    let dialogRef = this.dialog.open(TeamViewFiltersComponent, {
      height: 'fit-content',
      width: 'fit-content',
      panelClass: 'claro-custom-dialog',
      data: {},
    });

    let componentInstance = dialogRef.componentInstance;
    componentInstance.filterDataList = mgrsList;
    componentInstance.filterHeader = this.translate.instant(
      this.genderSerivce.transform('teamView.managerFilterHeader')
    );

    dialogRef.afterClosed().subscribe((selectedValue) => {
      this.filterByManager(selectedValue);
    });
  }

  changeManager(managerId: string): void{
    this.filterByManager(managerId);
  }

  changeOrg(orgId:string): void{
    this.filterByOrg(orgId);
  }

  filterByManager(managerId: string): void {
    if (managerId == undefined || managerId == '') {
      // managerId is undefiened - no manager was selected
      managerId = '';
    }

    this.selectedManagerId = managerId;

    this.getAggData();
  }

  filterByOrg(orgId: string): void {
    this.localStorage.write(LocalStorageKeys.resellerSelectedOrg, orgId);
    this.getAggData();
  }

  getOrgOverride(): string {
    if (!this.user?.isReseller) {
      return '';
    }

    const orgDetails: string | undefined = this.localStorage.getSecured(
      LocalStorageKeys.resellerSelectedOrg
    );

    if (orgDetails) {
      if (orgDetails == FilterOrgValueOptions.EMPTY){
        // no org filter
        return '';
      }
      return orgDetails;
    } else {
      return '';
    }
  }

  onCultureBreakdownClosed(): void {
    this.sideNavRef.close();
  }

  ngOndestroy(): void {
    // unsubscribe from the data service to avoid memory leaks
    this.dataSubscription.unsubscribe();
  }
}